import {
  Modal,
  type ModalProps,
  Stack,
  ToggleSwitch,
  Typography,
} from '@omnipresentgroup/design-system';
import type { MergeIntegrationEmployee } from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';

import { MergeEmployeeList } from './MergeEmployeesList';

type MergeFillInWorkEmailsActionModalProps = {
  modalProps: Omit<ModalProps, 'children'>;
  handleClose: () => void;
  employees: MergeIntegrationEmployee[];
};

const TITLE = 'Action required';
const DESCRIPTION =
  'There are employees with missing work emails. Please fill in their work emails to proceed.';

export const MergeWorkEmailsActionModal = ({
  modalProps,
  handleClose,
  employees,
}: MergeFillInWorkEmailsActionModalProps) => {
  const [visibleEmployees, setVisibleEmployees] =
    useState<MergeIntegrationEmployee[]>(employees);
  const [checked, setChecked] = useState(false);

  const enablePagination = useMemo(
    () => visibleEmployees.length > 10,
    [visibleEmployees.length],
  );

  const onCheckedChange = (isChecked: boolean) => {
    setChecked(isChecked);
    if (isChecked) {
      const employeesWithMissingWorkEmail = employees.filter(
        (employee) => !employee.workEmail || employee.workEmail.trim() === '',
      );
      setVisibleEmployees(employeesWithMissingWorkEmail);
    } else {
      setVisibleEmployees(employees);
    }
  };

  return (
    <Modal {...modalProps} span={6}>
      <Modal.Header title={TITLE} onClose={handleClose} />
      <Modal.Body>
        <Stack>
          <Stack gap="4">
            <Typography as="span" size="14">
              {DESCRIPTION}
            </Typography>
            <ToggleSwitch
              id="missing-work-emails-toggle"
              label="Employees with missing work emails"
              size="small"
              checked={checked}
              onCheckedChange={onCheckedChange}
            />
          </Stack>
          <MergeEmployeeList
            employees={visibleEmployees}
            columnVisibility={{
              employeeName: true,
              integrationStatus: true,
            }}
            usePagination={enablePagination}
          />
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
